import React, { useState, useEffect } from 'react';
import { Typography, Box, Grid, Paper, Snackbar, Alert } from '@mui/material';
import { 
  Business, 
  Description, 
  Receipt, 
  EuroSymbol,
  TrendingUp,
  TrendingDown 
} from '@mui/icons-material';
import StatsCard from '../components/StatsCard';
import { styled } from '@mui/material/styles';
import { companiesService, quotesService, invoicesService } from '../services/api';
import { subscriptionService } from '../services/api';
import { loadCompanyProfile } from '../utils/companyProfileStorage';

const GlowingContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, transparent 0%, rgba(139, 115, 85, 0.02) 100%)',
  borderRadius: 32,
  padding: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(139, 115, 85, 0.05) 0%, transparent 50%, rgba(139, 115, 85, 0.05) 100%)',
    opacity: 0.5,
    animation: 'pulse 4s ease-in-out infinite',
  },
  
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.3 },
    '50%': { opacity: 0.7 },
  },
}));

const WelcomeCard = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #2F2F2F 0%, #4A4A4A 50%, #5A5A5A 100%)',
  color: '#ffffff',
  borderRadius: 24,
  padding: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0px 20px 40px rgba(47, 47, 47, 0.4), 0px 0px 40px rgba(139, 115, 85, 0.3)',
  border: '2px solid rgba(139, 115, 85, 0.3)',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -50,
    right: -50,
    width: 200,
    height: 200,
    background: 'radial-gradient(circle, rgba(139, 115, 85, 0.2) 0%, transparent 70%)',
    borderRadius: '50%',
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -30,
    left: -30,
    width: 150,
    height: 150,
    background: 'radial-gradient(circle, rgba(139, 115, 85, 0.1) 0%, transparent 70%)',
    borderRadius: '50%',
  },
}));

const Dashboard = () => {
  const [companyProfile, setCompanyProfile] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [stats, setStats] = useState([
    { 
      title: 'Entreprises', 
      value: '0', 
      icon: <Business />, 
      color: 'primary',
      subtitle: 'Clients actifs',
      trend: { positive: true, text: '+0 ce mois', icon: <TrendingUp /> }
    },
    { 
      title: 'Devis', 
      value: '0', 
      icon: <Description />, 
      color: 'success',
      subtitle: 'En cours',
      trend: { positive: true, text: '+0 ce mois', icon: <TrendingUp /> }
    },
    { 
      title: 'Factures', 
      value: '0', 
      icon: <Receipt />, 
      color: 'warning',
      subtitle: 'Émises',
      trend: { positive: false, text: 'Aucune facture', icon: <TrendingDown /> }
    },
    { 
      title: 'CA Total', 
      value: '0 €', 
      icon: <EuroSymbol />, 
      color: 'info',
      subtitle: 'Chiffre d\'affaires',
      trend: { positive: true, text: '+0% vs mois dernier', icon: <TrendingUp /> }
    }
  ]);

  // Détecter le retour de Stripe avec succès
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setShowSuccessMessage(true);

      // Fallback: si le webhook n'a pas été reçu (tunnel down, délai Stripe), on synchronise via l'API
      subscriptionService.syncCheckoutSession(sessionId)
        .then(() => {
          window.dispatchEvent(new Event('subscription:refresh'));
        })
        .catch((e) => {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Sync checkout session failed:', e?.message || e);
          }
          // On laisse le webhook faire si jamais il arrive plus tard
        });

      // Nettoyer l'URL
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, []);

  // Charger le profil de l'entreprise
  useEffect(() => {
    const storedProfile = loadCompanyProfile();
    if (storedProfile) {
      setCompanyProfile(storedProfile);
    }
  }, []);

  // Charger les statistiques réelles depuis l'API
  useEffect(() => {
    const loadStats = async () => {
      try {
        // Charger les données depuis l'API MySQL avec authentification
        const [companies, quotes, invoices] = await Promise.all([
          companiesService.getAll(),
          quotesService.getAll(),
          invoicesService.getAll()
        ]);

        // Calculer le CA total (factures payées)
        const totalRevenue = invoices
          .filter(inv => inv.status === 'PAID')
          .reduce((sum, inv) => sum + (inv.totalTTC || 0), 0);

        // Calculer les tendances (exemple simple)
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        
        const thisMonthQuotes = quotes.filter(q => {
          const quoteDate = new Date(q.date);
          return quoteDate.getMonth() === currentMonth && quoteDate.getFullYear() === currentYear;
        }).length;

        const thisMonthInvoices = invoices.filter(i => {
          const invoiceDate = new Date(i.date);
          return invoiceDate.getMonth() === currentMonth && invoiceDate.getFullYear() === currentYear;
        }).length;

        setStats([
          { 
            title: 'Entreprises', 
            value: companies.length.toString(), 
            icon: <Business />, 
            color: 'primary',
            subtitle: 'Clients actifs',
            trend: { positive: true, text: `${companies.length} client${companies.length > 1 ? 's' : ''}`, icon: <TrendingUp /> }
          },
          { 
            title: 'Devis', 
            value: quotes.length.toString(), 
            icon: <Description />, 
            color: 'success',
            subtitle: 'En cours',
            trend: { positive: thisMonthQuotes >= 0, text: `+${thisMonthQuotes} ce mois`, icon: thisMonthQuotes > 0 ? <TrendingUp /> : <TrendingDown /> }
          },
          { 
            title: 'Factures', 
            value: invoices.length.toString(), 
            icon: <Receipt />, 
            color: 'warning',
            subtitle: 'Émises',
            trend: { positive: thisMonthInvoices > 0, text: `+${thisMonthInvoices} ce mois`, icon: thisMonthInvoices > 0 ? <TrendingUp /> : <TrendingDown /> }
          },
          { 
            title: 'CA Total', 
            value: `${totalRevenue.toFixed(0)} €`, 
            icon: <EuroSymbol />, 
            color: 'info',
            subtitle: 'Chiffre d\'affaires',
            trend: { positive: totalRevenue > 0, text: totalRevenue > 0 ? `${totalRevenue.toFixed(0)}€ encaissés` : 'Aucun CA', icon: totalRevenue > 0 ? <TrendingUp /> : <TrendingDown /> }
          }
        ]);
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
        // En cas d'erreur, afficher des stats vides
        setStats([
          { title: 'Entreprises', value: '0', icon: <Business />, color: 'primary', subtitle: 'Clients actifs', trend: { positive: true, text: '+0 ce mois', icon: <TrendingUp /> } },
          { title: 'Devis', value: '0', icon: <Description />, color: 'success', subtitle: 'En cours', trend: { positive: false, text: '+0 ce mois', icon: <TrendingDown /> } },
          { title: 'Factures', value: '0', icon: <Receipt />, color: 'warning', subtitle: 'Émises', trend: { positive: false, text: 'Aucune facture', icon: <TrendingDown /> } },
          { title: 'CA Total', value: '0 €', icon: <EuroSymbol />, color: 'info', subtitle: 'Chiffre d\'affaires', trend: { positive: false, text: 'Aucun CA', icon: <TrendingDown /> } }
        ]);
      }
    };

    loadStats();
  }, []);

  return (
    <GlowingContainer>
      {/* Carte de bienvenue personnalisée */}
      <WelcomeCard sx={{ mb: 4 }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700,
              textAlign: 'center',
              mb: 1,
              color: '#ffffff',
              textShadow: '0px 2px 8px rgba(0, 0, 0, 0.3)',
             
            }}
          >
            Bienvenue {companyProfile?.name ? companyProfile.name : 'sur Facteio'} !
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.95)',
              fontSize: '1.1rem',
              textAlign: 'center'
            }}
          >
            {companyProfile?.name 
              ? `Gérez votre activité efficacement depuis votre tableau de bord`
              : `Configurez votre profil entreprise pour commencer`
            }
          </Typography>
        </Box>
      </WelcomeCard>

      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h1" 
          sx={{ 
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            textAlign: 'center',
            fontWeight: 800,
            color: '#C5A46D',
            textShadow: '0px 2px 4px rgba(31, 41, 55, 0.2)',
            position: 'relative',
            
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -10,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '200px',
              height: '4px',
              background: 'linear-gradient(135deg, #5E564F 0%, #8B8178 100%)',
              borderRadius: '2px',
              boxShadow: '0px 2px 8px rgba(94, 86, 79, 0.25)'
            }
          }}
        >
          Tableau de bord
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            textAlign: 'center',
            fontWeight: 500,
            color: '#696967',
            textShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
            mt: 2
          }}
        >
          Gestion professionnelle de vos devis et factures
        </Typography>
      </Box>
      
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <StatsCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
              subtitle={stat.subtitle}
              trend={stat.trend}
              onClick={() => {}}
            />
          </Grid>
        ))}
      </Grid>

      <WelcomeCard elevation={0}>
        <Box position="relative" zIndex={1}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 700,
              color: '#ffffff',
              textShadow: '0px 2px 8px rgba(0, 0, 0, 0.5)',
              background: 'linear-gradient(135deg, #C5A46D 0%, #D2B07C 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.3))',
              textAlign: 'center'
            }}
          >
            Bienvenue dans votre logiciel de gestion de devis et factures
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2,
              fontWeight: 600,
              color: '#ffffff',
              textShadow: '0px 1px 4px rgba(0, 0, 0, 0.4)',
              textAlign: 'center'
            }}
          >
            Votre solution complète pour la gestion de devis et factures
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#f5f5f5',
              lineHeight: 1.7,
              fontSize: '1.1rem',
              textShadow: '0px 1px 3px rgba(0, 0, 0, 0.3)',
              fontWeight: 400,
              textAlign: 'center'
            }}
          >
            Créez des devis professionnels, gérez vos clients et suivez votre chiffre d'affaires en toute simplicité. 
            Votre logiciel est prêt à l'emploi !
          </Typography>
        </Box>
      </WelcomeCard>

      {/* Message de succès après paiement Stripe */}
      <Snackbar 
        open={showSuccessMessage} 
        autoHideDuration={8000} 
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: { xs: 72, sm: 88 },
        }}
      >
        <Alert 
          onClose={() => setShowSuccessMessage(false)} 
          severity="success" 
          variant="filled"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 600,
            backgroundColor: '#1F2937',
            textAlign: 'center',
            alignItems: 'center',
            maxWidth: 900,
            width: { xs: 'calc(100vw - 24px)', sm: 'auto' },
            '& .MuiAlert-icon': {
              fontSize: '2rem'
            }
          }}
        >
          🎉 Félicitations ! Vous avez souscrit au forfait Professionnel ! Vous êtes maintenant illimité en devis, factures et clients. Profitez pleinement de toutes les fonctionnalités ! ✨
        </Alert>
      </Snackbar>

    </GlowingContainer>
  );
};

export default Dashboard;
